import type { MeetingAttendance, MeetingAttendanceStatus } from '@repo/types/meetings';

import { useEditAttendance } from '@repo/api/meeting/useEditAttendance';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { BookmarkPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import MeetingAttendanceIncompleteAlert from '@/components/features/meeting/MeetingAttendanceIncompleteAlert';
import MeetingAttendanceToggles from '@/components/features/meeting/MeetingAttendanceToggles';
import UserCard from '@/components/features/user/UserCard';
import { Button } from '@/components/ui/button';
import { EmptyState, EmptyStateContent, EmptyStateDescription } from '@/components/ui/empty-state';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

interface MeetingAttendanceProps {
  attendance: MeetingAttendance[];
  meetingId: number;
}

export default function MeetingAttendance(meetingAttendanceProps: MeetingAttendanceProps) {
  const { attendance, meetingId } = meetingAttendanceProps;

  const isEmpty: boolean = attendance.length === 0;

  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [attendanceData, setAttendanceData] = useState<MeetingAttendance[]>(attendance);
  const [isIncompleteAlertOpen, setIsIncompleteAlertOpen] = useState<boolean>(false);

  const unmarkedCount = attendanceData.filter((entry) => entry.status === null).length;

  const setAllPresent = () => {
    setAttendanceData((prev) => prev.map((a) => ({ ...a, status: 'present' })));
    setHasChanges(true);
  };

  const { isPending, mutateAsync } = useEditAttendance();

  const handleAttendanceChange = (newStatus: MeetingAttendanceStatus, userId: number) => {
    setAttendanceData((prevData) => {
      return prevData.map((a) => (a.userId == userId ? { ...a, status: newStatus } : a));
    });
    setHasChanges(true);
  };
  const onCancel = () => {
    setAttendanceData(attendance);
    setHasChanges(false);
  };

  const save = (data: MeetingAttendance[]) => {
    mutateAsync({
      id: meetingId,
      attendanceData: data,
    })
      .then(() => {
        setHasChanges(false);
        toast.success('Frekwencja została zapisana');
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  // Saving a half-marked list would record silence as a decision, so the
  // teacher is asked to complete it rather than being blocked by a dead button.
  const onSubmit = () => {
    if (unmarkedCount > 0) {
      setIsIncompleteAlertOpen(true);
      return;
    }
    save(attendanceData);
  };

  const onMarkRestPresent = () => {
    const completed: MeetingAttendance[] = attendanceData.map((entry) => (entry.status === null ? { ...entry, status: 'present' } : entry));
    setAttendanceData(completed);
    setHasChanges(true);
    setIsIncompleteAlertOpen(false);
    save(completed);
  };

  useEffect(() => {
    if (attendanceData !== attendance) {
      setAttendanceData(attendance);
    }
  }, [attendance]);

  return (
    <div className={'flex h-full w-full grow flex-col divide-y'}>
      <header className={'flex items-center justify-between gap-2 p-4'}>
        <div className={'flex h-10 items-center'}>
          <h3 className={'w-full truncate text-lg font-semibold'}>Lista obecności</h3>
        </div>
        <Button onClick={setAllPresent} size={'sm'} variant={'outline'} disabled={isPending || isEmpty}>
          <BookmarkPlus />
        </Button>
      </header>
      <div className={'h-full w-full overflow-auto p-4'}>
        {isEmpty && (
          <EmptyState>
            <EmptyStateContent>
              <EmptyStateDescription>Brak uczestników spotkania</EmptyStateDescription>
            </EmptyStateContent>
          </EmptyState>
        )}
        {attendanceData.map((attendance) => {
          return (
            <div className={'flex w-full items-center justify-between gap-2'} key={attendance.userId}>
              <UserCard user={attendance.user} />
              <MeetingAttendanceToggles status={attendance.status} userId={attendance.userId} onValueChange={handleAttendanceChange} />
            </div>
          );
        })}
      </div>
      {!isEmpty && (
        <div className={'flex justify-end gap-2 p-4'}>
          <Button disabled={!hasChanges || isPending} onClick={onCancel} variant={'tertiary'}>
            Anuluj
          </Button>
          <Button disabled={(!hasChanges && unmarkedCount === 0) || isPending} onClick={onSubmit} variant={'secondary'}>
            <LoadingSwap isLoading={isPending}>Zapisz</LoadingSwap>
          </Button>
        </div>
      )}

      <MeetingAttendanceIncompleteAlert open={isIncompleteAlertOpen} onOpenChange={setIsIncompleteAlertOpen} unmarkedCount={unmarkedCount} onMarkRestPresent={onMarkRestPresent} />
    </div>
  );
}
